"use client";

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useModalDialog } from "./use-modal-dialog";
import { createPortal } from "react-dom";
import styles from "./google-analytics-consent.module.css";
import {
  type GoogleAnalyticsConsent as ConsentChoice,
  normalizeGoogleAnalyticsMeasurementId,
  readGoogleAnalyticsConsent,
  revokeGoogleAnalytics,
  saveGoogleAnalyticsConsent,
  startGoogleAnalytics,
} from "./google-analytics";

interface GoogleAnalyticsConsentProps {
  measurementId?: string | null;
  settingsPlacement?: "floating" | "inline";
}

const PREFERENCES_QUERY_KEY = "analytics";
const PREFERENCES_QUERY_VALUE = "preferences";

export function GoogleAnalyticsConsent({
  measurementId,
  settingsPlacement = "floating",
}: GoogleAnalyticsConsentProps) {
  const normalizedId = normalizeGoogleAnalyticsMeasurementId(measurementId);
  const isHydrated = useSyncExternalStore(subscribeToHydration, getClientSnapshot, getServerSnapshot);
  const [choiceOverride, setChoiceOverride] = useState<ConsentChoice>();
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const isRecording = isHydrated && isRecordingSurface();
  const preferenceRequested = isHydrated && isPreferenceRequest();
  const choice = choiceOverride ?? (isHydrated ? readGoogleAnalyticsConsent() : null);
  const isOpen = openOverride ?? (preferenceRequested || (Boolean(normalizedId) && choice === null));
  const isSettingsPanel = choice !== null || openOverride === true || !normalizedId;
  const panel = useRef<HTMLElement>(null);
  const close = useCallback(() => { setOpenOverride(false); clearPreferenceRequest(); }, []);
  useModalDialog(isHydrated && !isRecording && isOpen && isSettingsPanel, panel, close);

  useEffect(() => {
    if (normalizedId && isHydrated && !isRecording && choice === "granted" && !preferenceRequested) {
      startGoogleAnalytics(normalizedId);
    }
  }, [choice, isHydrated, isRecording, normalizedId, preferenceRequested]);

  if (!isHydrated || isRecording) return null;

  const choose = (nextChoice: ConsentChoice) => {
    const withdrawingConsent = choice === "granted" && nextChoice === "denied";
    saveGoogleAnalyticsConsent(nextChoice);
    setChoiceOverride(nextChoice);
    setOpenOverride(false);
    clearPreferenceRequest();

    if (nextChoice === "granted") {
      startGoogleAnalytics(normalizedId);
      return;
    }

    revokeGoogleAnalytics();
    if (withdrawingConsent) window.location.reload();
  };

  const settingsButton = (
      <button
        aria-label="Open analytics settings"
        className={`${styles.preferences} ${settingsPlacement === "inline" ? styles.inlinePreferences : ""}`}
        type="button"
        onClick={() => setOpenOverride(true)}
      >
        {settingsPlacement === "inline" ? "ANALYTICS" : "ANALYTICS SETTINGS"}
      </button>
    );
  if (!isOpen) return <>{settingsButton}</>;

  const consentPanel = (
    <aside
      ref={panel}
      className={`${styles.banner} ${isSettingsPanel ? styles.settingsPanel : ""}`}
      aria-labelledby="analytics-consent-title"
      aria-modal={isSettingsPanel || undefined}
      role="dialog"
    >
      <div className={styles.copy}>
        <strong id="analytics-consent-title">OPTIONAL ANALYTICS</strong>
        {normalizedId ? <span>
          Help us understand visits and improve the show. Google Analytics only loads after you
          allow it, and we keep advertising features off. <a href="/privacy/">Privacy notice</a>
        </span> : <span>Analytics is disabled in this preview. No analytics data is sent.</span>}
      </div>
      <div className={styles.actions}>
        {normalizedId && <button className={styles.allow} type="button" onClick={() => choose("granted")}>
          ALLOW ANALYTICS
        </button>}
        {normalizedId && <button className={styles.decline} type="button" onClick={() => choose("denied")}>
          NO THANKS
        </button>}
        <button className={styles.decline} type="button" data-modal-close onClick={close}>CLOSE</button>
      </div>
    </aside>
  );

  if (isSettingsPanel) {
    return <>{settingsButton}{createPortal(
      <div className={styles.settingsBackdrop}>{consentPanel}</div>,
      document.body,
    )}</>;
  }

  return <>{settingsButton}{createPortal(consentPanel, document.body)}</>;
}

function subscribeToHydration(): () => void {
  return () => {};
}

function getClientSnapshot(): boolean {
  return true;
}

function getServerSnapshot(): boolean {
  return false;
}

function isPreferenceRequest(): boolean {
  return new URLSearchParams(window.location.search).get(PREFERENCES_QUERY_KEY) ===
    PREFERENCES_QUERY_VALUE;
}

function clearPreferenceRequest(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get(PREFERENCES_QUERY_KEY) !== PREFERENCES_QUERY_VALUE) return;
  url.searchParams.delete(PREFERENCES_QUERY_KEY);
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

function isRecordingSurface(): boolean {
  const path = window.location.pathname.toLowerCase();
  const query = new URLSearchParams(window.location.search);
  return /^\/record-(portrait|square|landscape)\/?$/.test(path) || query.has("record");
}
